import React from 'react';
import { PoundSterling } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PAYMENT_METHODS, BalanceBreakdown } from '@/components/training/paymentHistory';

/** Edit a trainee's course fee / paid-so-far figures. */
export function EditPaymentsDialog({ editReg, setEditReg, editForm, setEditForm, busy, saveTraineeEdit, palette }) {
  return (
    <Dialog open={!!editReg} onOpenChange={(o) => !o && !busy && setEditReg(null)}>
      <DialogContent
        data-testid="training-edit-balance-modal"
        className="!bg-white max-w-sm"
        style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: palette.dark }}>Payments</DialogTitle>
          <DialogDescription>
            {editReg?.name} · {editReg?.email}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-total">Course fee (£)</Label>
              <Input
                id="edit-total"
                data-testid="edit-payment-total-input"
                type="number"
                min="0"
                step="0.01"
                value={editForm.payment_total}
                onChange={(e) => setEditForm(f => ({ ...f, payment_total: e.target.value }))}
                placeholder="0.00"
                autoFocus
                className="bg-white"
              />
            </div>
            <div>
              <Label htmlFor="edit-received">Paid so far (£)</Label>
              <Input
                id="edit-received"
                data-testid="edit-payment-received-input"
                type="number"
                min="0"
                step="0.01"
                value={editForm.payment_received}
                onChange={(e) => setEditForm(f => ({ ...f, payment_received: e.target.value }))}
                placeholder="0.00"
                className="bg-white"
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">Balance owed is calculated automatically. Setting both to 0 clears the fee.</p>
          {editReg && <BalanceBreakdown reg={editReg} form={editForm} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditReg(null)} disabled={busy}>Cancel</Button>
          <Button onClick={saveTraineeEdit} disabled={busy} data-testid="edit-balance-save" className="text-white font-bold" style={{ background: palette.primary }}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Record a single payment (amount + method) against a trainee's balance. */
export function RecordPaymentDialog({ paymentReg, setPaymentReg, paymentForm, setPaymentForm, paymentBusy, recordPayment, palette }) {
  return (
    <Dialog open={!!paymentReg} onOpenChange={(o) => !o && !paymentBusy && setPaymentReg(null)}>
      <DialogContent
        data-testid="record-payment-modal"
        className="!bg-white max-w-sm"
        style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}
      >
        <DialogHeader>
          <DialogTitle style={{ color: palette.dark }}>Record payment</DialogTitle>
          <DialogDescription>
            {paymentReg?.name} · £{Number(paymentReg?.balance_owed || 0).toFixed(2)} outstanding of £{Number(paymentReg?.payment_total || 0).toFixed(2)} course fee
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="payment-amount">Amount paid (£)</Label>
            <Input
              id="payment-amount"
              data-testid="payment-amount-input"
              type="number"
              min="0.01"
              step="0.01"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
              autoFocus
              className="bg-white mt-1"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Pre-filled with the outstanding balance. Adjust if they&apos;re only paying part.
            </p>
          </div>
          <div>
            <Label>Payment method</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setPaymentForm(f => ({ ...f, method: m.key }))}
                  data-testid={`payment-method-${m.key}`}
                  className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                    paymentForm.method === m.key
                      ? 'text-white shadow-sm'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  style={paymentForm.method === m.key
                    ? { background: palette.primary, borderColor: palette.primary }
                    : undefined}
                  aria-pressed={paymentForm.method === m.key}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPaymentReg(null)} disabled={paymentBusy}>Cancel</Button>
          <Button
            onClick={recordPayment}
            disabled={paymentBusy || !paymentForm.amount || !paymentForm.method}
            data-testid="record-payment-submit"
            className="text-white font-bold"
            style={{ background: palette.primary }}
          >
            <PoundSterling className="w-4 h-4 mr-1" />
            {paymentBusy ? 'Recording…' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
